import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import { objectivePartsSchema, ObjectivePartsValidationSchema } from "../../schemas/objectiveParts.schema";
import useCreateObjectivePart from "../../hooks/useCreateMaintenanceObjectivePart";
import SelectController from "@/common/components/SelectController";
import { useMaintenanceObjectiveSystems } from "../../../objectiveSystems/hooks/useMaintenanceObjectiveSystems";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { MaintenanceObjectiveSystemData } from "../../../objectiveSystems/types/MaintenanceObjectiveSystem.type";

const MaintenanceObjectivePartsCreate = () => {
  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchSystem, setSearchSystem] = useDebounce('');

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const {
    data: objectiveSystemsOptions,
  } = useMaintenanceObjectiveSystems({
    filters: {
      search: searchSystem,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveSystemData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const {
    mutate: createObjectivePart,
    isPending: isCreateObjectivePartPending,
  } = useCreateObjectivePart({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceObjectiveParts'] });
      goBack({ fallback: `/objective-maintenance/parts` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ObjectivePartsValidationSchema>({
    resolver: zodResolver(objectivePartsSchema),
  });

  const onSubmit: SubmitHandler<ObjectivePartsValidationSchema> = (data) => createObjectivePart(data);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={
        hasUserPermission('Admin::Maintenance::ObjectiveParts::Create') || isUserPermissionsPending
          ? 200
          : 403
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/objective-maintenance/parts` }}
          titleClassName="text-nowrap max-w-56 overflow-hidden text-ellipsis font-normal text-base"
          title="Objetos de manutenção"
          subtitle="Nova Parte"
          buttons={(
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                onClick={() => goBack({ fallback: `/objective-maintenance/parts` })}
                disabled={isCreateObjectivePartPending}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                name="submit"
                isLoading={isCreateObjectivePartPending}
              >
                Criar Parte
              </Button>
            </div>
          )}
        />

        <Box className="max-h-[calc(100vh-132px)] overflow-y-auto flex flex-col gap-4">
          <p className="text-lg font-medium">
            Dados gerais da parte
          </p>

          <div className="flex items-start gap-3">
            <SelectController
              control={control}
              name="system_parts_attributes"
              label="Sistema"
              options={objectiveSystemsOptions}
              onInputChange={setSearchSystem}
              className="flex-1"
              markAsRequired
              isMulti
              errorMessage={errors.system_parts_attributes?.message || undefined}
            />

            <TextField
              className="flex-1"
              label="Nome da parte"
              maxLength={32}
              {...register('display_name')}
              disabled={isCreateObjectivePartPending}
              errorMessage={errors.display_name?.message || undefined}
              markAsRequired
            />
          </div>
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </ErrorMessageWrapper>
  );
}

export default MaintenanceObjectivePartsCreate;
